import React, { useEffect, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import { useTranslation } from 'react-i18next';
import { APP_LANGUAGES, resolveAppLanguage } from '../constants/languages';
import { DOCUMENT } from '../constants/documentTheme';
import { applyAppLanguage } from '../utils/appLanguage';
import api from '../config/api';

const persistLanguagePreference = async (languageCode) => {
  const token = typeof window !== 'undefined' ? window.localStorage.getItem('token') : null;
  if (!token) return;
  try {
    const response = await api.get('/preferences');
    await api.put('/preferences', {
      preferences: {
        ...response.data,
        preferredLanguage: languageCode,
      },
    });
  } catch {
    // Local language still applies if the preference save fails.
  }
};

const NavLanguageSelector = () => {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [current, setCurrent] = useState(() => (
    resolveAppLanguage(i18n.resolvedLanguage || i18n.language)
  ));

  useEffect(() => {
    setCurrent(resolveAppLanguage(i18n.resolvedLanguage || i18n.language));
  }, [i18n.resolvedLanguage, i18n.language]);

  const handleSelect = (code) => {
    const next = resolveAppLanguage(code);
    setCurrent(next);
    setAnchorEl(null);
    applyAppLanguage(next, i18n).then((applied) => {
      persistLanguagePreference(applied);
    });
  };

  return (
    <>
      <IconButton
        data-testid="nav-language-selector"
        aria-label={t('nav.language')}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          color: 'rgba(246,239,228,.7)',
          width: { xs: 36, md: 40 },
          height: { xs: 36, md: 40 },
          flexShrink: 0,
          borderRadius: 0,
          '&:hover': {
            color: DOCUMENT.navAccent,
            backgroundColor: 'transparent',
          },
        }}
      >
        <TranslateIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 0,
              boxShadow: 'none',
              border: `1px solid ${DOCUMENT.ruleHard}`,
              bgcolor: DOCUMENT.surface,
              minWidth: 220,
            },
          },
        }}
      >
        {APP_LANGUAGES.map((language) => {
          const selected = language.code === current;
          return (
            <MenuItem
              key={language.code}
              selected={selected}
              onClick={() => handleSelect(language.code)}
              sx={{
                py: 1.25,
                fontFamily: DOCUMENT.sans,
                '&.Mui-selected': {
                  bgcolor: 'rgba(138,106,40,0.08)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography component="span" sx={{ fontSize: 18, lineHeight: 1 }}>
                  {language.flag}
                </Typography>
                <Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: selected ? 600 : 500, color: DOCUMENT.ink }}>
                    {language.nativeName}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: DOCUMENT.ink3 }}>
                    {language.name}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default NavLanguageSelector;
